import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './Context/AuthContext';
import { CartProvider } from './Context/CartContext';
import { WishlistProvider } from './Context/WishlistContext';
import { CurrencyProvider } from './Context/CurrencyContext';
import Home from './Components/Home';
import Shop from './Components/Shop';
import About from './Components/About';
import ProductDetail from './Components/ProductDetail';
import Cart from './Components/Cart';
import Checkout from './Components/Checkout';
import PaymentReturn from './Components/PaymentReturn';
import Login from './Components/Login';
import Signup from './Components/Signup';
import Wishlist from './Components/Wishlist';
import Orders from './Components/Orders';
import Dashboard from './Components/Dashboard';
import ProtectedRoute from './Components/ProtectedRoute';
import ServicePage from './Components/ServicePage';
import { GOOGLE_CLIENT_ID } from './constants/googleAuth';
import { LEGACY_CATEGORY_IDS } from './constants/shopCategories';
import ChatBot from './Components/ChatBot';
import SessionTimeout from './Components/SessionTimeout';
import MotionLayer from './Components/MotionLayer';
import './App.css';

/** Category pages reuse the shop layout with that category fixed (key: fresh state per category). */
function CollectionPage() {
  const { slug } = useParams();
  // Old sub-category links open the category that now holds those products
  if (LEGACY_CATEGORY_IDS[slug]) return <Navigate to={`/collections/${LEGACY_CATEGORY_IDS[slug]}`} replace />;
  return <Shop key={slug} collectionSlug={slug} />;
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AuthProvider>
        <CartProvider>
          <WishlistProvider>
            <CurrencyProvider>
            <Router>
              {/* Scroll reveals, pointer tilt and the scroll-progress bar */}
              <MotionLayer />
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/about" element={<About />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/collections/:slug" element={<CollectionPage />} />
                {/* Service pages (Starlink, Digital Security, Network, Canal+ & DStv, Software, Training) */}
                <Route path="/services/:slug" element={<ServicePage />} />
                <Route path="/product-details/:id" element={<ProductDetail />} />
                <Route path="/cart" element={<Cart />} />
                <Route
                  path="/checkout"
                  element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  }
                />
                {/* Customers land here after the card payment page */}
                <Route path="/payment/return" element={<PaymentReturn />} />
                <Route path="/wishlist" element={<Wishlist />} />
                {/* Signed-in customers' own orders and their status */}
                <Route
                  path="/orders"
                  element={
                    <ProtectedRoute>
                      <Orders />
                    </ProtectedRoute>
                  }
                />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route
                  path="/dashboard"
                  element={
                    <ProtectedRoute>
                      <Dashboard />
                    </ProtectedRoute>
                  }
                />
              </Routes>
              <ChatBot />
              {/* Inactivity sign-out and session refresh for signed-in users */}
              <SessionTimeout />
            </Router>
            </CurrencyProvider>
          </WishlistProvider>
        </CartProvider>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
